/**
 * One training agent session (TRA-1327): its header with the stop control,
 * the live transcript, the composer and the details panel. The transcript
 * follows the session's event stream, so its status leads the list's,
 * which refreshes on a timer.
 */

import { Send, Square } from 'lucide-react'
import { useEffect, useId, useMemo, useRef, useState } from 'react'

import type { TrainingAgentSessionView } from '@/api/generated/model'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  useDecideAgentApproval,
  useDecideLaunchRequest,
  useSendAgentMessage,
  useSessionEvents,
  useStopAgentSession,
} from '@/hooks/useTrainingAgent'
import type { SessionEvents } from '@/hooks/useTrainingAgent'
import { formatRelativeTime } from '@/lib/utils'

import { STATUS_BADGE, formatUsd } from './display'
import { MESSAGE_MAX_CHARS } from './newSession'
import { SessionDetails } from './SessionDetails'
import { buildTranscript, isLive } from './transcript'
import type { SessionStatus, TranscriptItem } from './transcript'
import { AgentRow, ApprovalCard, LaunchCard, NoteRow, ToolRows, UserRow } from './TranscriptItems'
import type { ApprovalActions, LaunchActions } from './TranscriptItems'

const LABEL = 'font-mono text-[10px] uppercase tracking-wider text-muted-foreground'

const PLACEHOLDER: Record<SessionStatus, string> = {
  starting: 'Message the agent',
  idle: 'Message the agent',
  working: 'The agent is working',
  approval: 'Answer the approval request to continue',
  ended: 'This session has ended',
  failed: 'This session failed',
}

function SessionHeader({
  session,
  status,
  readOnly,
}: {
  session: TrainingAgentSessionView
  status: SessionStatus
  readOnly: boolean
}): React.JSX.Element {
  const stop = useStopAgentSession()
  const badge = STATUS_BADGE[status]
  return (
    <div className="space-y-1 border-b border-border p-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="min-w-0 break-words font-serif text-lg text-foreground">{session.title}</h2>
        <Badge variant={badge.variant}>{badge.label}</Badge>
        {isLive(status) && !readOnly && (
          <Button
            variant="outline"
            size="sm"
            className="ml-auto"
            disabled={stop.isPending}
            onClick={() => {
              stop.mutate({ sessionId: session.id })
            }}
          >
            <Square />
            Stop session
          </Button>
        )}
      </div>
      {/* Data, so normal case: an email and amounts are not labels. */}
      <p className="font-mono text-xs text-muted-foreground">
        {session.created_by} · {formatRelativeTime(session.created_at)} ·{' '}
        {formatUsd(session.cost_usd)} of {formatUsd(session.budget_usd)}
      </p>
    </div>
  )
}

function Composer({
  sessionId,
  status,
  readOnly,
}: {
  sessionId: string
  status: SessionStatus
  readOnly: boolean
}): React.JSX.Element {
  const [draft, setDraft] = useState('')
  const send = useSendAgentMessage()
  const id = useId()
  const open = !readOnly && (status === 'idle' || status === 'starting')
  const text = draft.trim()
  const tooLong = text.length > MESSAGE_MAX_CHARS
  const sendable = open && text !== '' && !tooLong && !send.isPending

  function submit(): void {
    if (!sendable) return
    // The box empties as the message leaves, so what someone types while it
    // is on its way is only the new text. A failed send puts the message
    // back ahead of that text.
    setDraft('')
    send.mutate(
      { sessionId, data: { text } },
      {
        onError: () => {
          setDraft((current) => (current === '' ? text : `${text}\n${current}`))
        },
      },
    )
  }

  return (
    <form
      className="space-y-1 border-t border-border p-3"
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      <div className="flex items-end gap-2">
        <Label htmlFor={id} className="sr-only">
          Message the agent
        </Label>
        <Textarea
          id={id}
          rows={2}
          value={draft}
          disabled={!open}
          placeholder={readOnly ? 'Impersonation is read-only' : PLACEHOLDER[status]}
          className="min-h-0 resize-none"
          aria-invalid={tooLong}
          aria-describedby={tooLong ? `${id}-error` : undefined}
          onChange={(event) => {
            setDraft(event.target.value)
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' || event.shiftKey) return
            event.preventDefault()
            submit()
          }}
        />
        <Button type="submit" disabled={!sendable}>
          <Send />
          Send
        </Button>
      </div>
      {tooLong && (
        <p id={`${id}-error`} className="text-xs text-destructive">
          A message holds up to {MESSAGE_MAX_CHARS.toLocaleString('en-US')} characters, and this
          one has {text.length.toLocaleString('en-US')}.
        </p>
      )}
    </form>
  )
}

/** Keep the newest row in view while the reader is at the bottom. */
function useStickToBottom(size: number): React.RefObject<HTMLDivElement> {
  const ref = useRef<HTMLDivElement>(null)
  const pinned = useRef(true)
  useEffect(() => {
    const element = ref.current
    if (element === null) return
    const onScroll = (): void => {
      pinned.current = element.scrollHeight - element.scrollTop - element.clientHeight < 80
    }
    element.addEventListener('scroll', onScroll)
    return () => {
      element.removeEventListener('scroll', onScroll)
    }
  }, [])
  useEffect(() => {
    const element = ref.current
    if (element !== null && pinned.current) element.scrollTop = element.scrollHeight
  }, [size])
  return ref
}

function StreamNotice({ stream, empty }: { stream: SessionEvents; empty: boolean }): React.JSX.Element | null {
  if (stream.state === 'connecting' && empty) {
    return <p role="status" className={LABEL}>Loading</p>
  }
  if (stream.error !== null) {
    const refused = stream.state === 'closed'
    return (
      <p role={refused ? 'alert' : 'status'} className={refused ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'}>
        {stream.error}
      </p>
    )
  }
  if (!empty) return null
  return (
    <div className="space-y-1 py-8 text-center">
      <h3 className="font-serif text-base text-foreground">Start with a question or a goal</h3>
      <p className="mx-auto max-w-md text-sm text-muted-foreground">
        Ask the agent about this team&apos;s training data and runs. Every command it wants to run
        waits for your approval.
      </p>
    </div>
  )
}

interface RowActions {
  approval: ApprovalActions
  launch: LaunchActions
}

function Row({ item, you, actions }: { item: TranscriptItem; you: string | null; actions: RowActions }): React.JSX.Element {
  switch (item.kind) {
    case 'user':
      return <UserRow turn={item} you={you} />
    case 'agent':
      return <AgentRow said={item} />
    case 'tools':
      return <ToolRows group={item} />
    case 'approval':
      return <ApprovalCard request={item} actions={actions.approval} />
    case 'launch':
      return <LaunchCard request={item} actions={actions.launch} />
    case 'note':
      return <NoteRow note={item} />
  }
}

function useApprovalActions(sessionId: string, decidable: boolean, over: boolean): ApprovalActions {
  const decide = useDecideAgentApproval()
  return {
    decide: decidable
      ? (approvalId, approve) => {
          decide.mutate({ sessionId, approvalId, data: { decision: approve ? 'approve' : 'decline' } })
        }
      : null,
    pending: decide.isPending,
    sessionOver: over,
  }
}

function useLaunchActions(sessionId: string, decidable: boolean, over: boolean): LaunchActions {
  const decide = useDecideLaunchRequest()
  return {
    decide: decidable
      ? (requestId, approve, reason) => {
          decide.mutate({
            sessionId,
            requestId,
            data: approve ? { decision: 'approve' } : { decision: 'decline', reason },
          })
        }
      : null,
    pending: decide.isPending,
    sessionOver: over,
  }
}

interface SessionViewProps {
  session: TrainingAgentSessionView
  /** Impersonation reads a session but cannot act in it. */
  readOnly: boolean
  /** The signed-in person's email, so their own messages read as "You". */
  you: string | null
}

/** Mount keyed by session id: the stream and the draft belong to one session. */
export function SessionView({ session, readOnly, you }: SessionViewProps): React.JSX.Element {
  const stream = useSessionEvents(session.id)
  const transcript = useMemo(() => buildTranscript(stream.events), [stream.events])
  const status = transcript.status ?? session.status
  const decidable = !readOnly && isLive(status)
  const actions: RowActions = {
    approval: useApprovalActions(session.id, decidable, !isLive(status)),
    launch: useLaunchActions(session.id, decidable, !isLive(status)),
  }
  const scroller = useStickToBottom(stream.events.length)
  return (
    <section aria-label="Session" className="flex min-h-0 flex-col">
      <SessionHeader session={session} status={status} readOnly={readOnly} />
      <div className="grid min-h-0 flex-1 xl:grid-cols-[minmax(0,1fr)_16rem]">
        <div className="flex min-h-0 flex-col">
          <div ref={scroller} role="log" aria-label="Transcript" className="min-h-[16rem] flex-1 space-y-4 overflow-y-auto p-4">
            <StreamNotice stream={stream} empty={transcript.items.length === 0} />
            {transcript.items.map((item) => (
              <Row key={item.seq} item={item} you={you} actions={actions} />
            ))}
            {status === 'working' && <p role="status" className={LABEL}>Agent working</p>}
          </div>
          <Composer sessionId={session.id} status={status} readOnly={readOnly} />
        </div>
        <SessionDetails session={session} tools={transcript.tools} />
      </div>
    </section>
  )
}
